import { useMemo, useState } from "react";
import {
    Ticket as TicketIcon,
    Mail,
    User,
    Users,
    CreditCard,
    Plus,
    Trash2,
    Loader2,
    CheckCircle2,
    AlertCircle,
    ExternalLink,
    CalendarDays,
    Clock,
    MapPin,
    Send,
    RefreshCw,
    Box,
} from "lucide-react";
import { Apirequests } from "../../utils/ApiRequests";
import { ErrorTost, SuccessTost } from "../../utils/ToastHelper";


const API_URL = "https://www.silvamethod.in/manage/api/v1/admin/generate-ticket";

function getToken() {
    try {
        return localStorage.getItem("token") || "";
    } catch {
        return "";
    }
}

const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || "").trim());

function Field({ label, icon: Icon, children, hint }) {
    return (
        <label className="block">
            <span className="mb-1.5 flex items-center gap-1.5 text-sm font-semibold text-slate-700">
                {Icon && <Icon className="h-4 w-4 text-slate-400" />} {label}
            </span>
            {children}
            {hint && <span className="mt-1 block text-xs text-slate-400">{hint}</span>}
        </label>
    );
}

function Toggle({ checked, onChange, label, desc, icon: Icon }) {
    return (
        <button
            type="button"
            onClick={() => onChange(!checked)}
            className="flex w-full items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white p-3 text-left transition hover:bg-slate-50"
        >
            <span className="flex items-center gap-2.5">
                {Icon && (
                    <span className={`flex h-9 w-9 items-center justify-center rounded-lg ${checked ? "bg-indigo-50 text-indigo-600" : "bg-slate-100 text-slate-400"}`}>
                        <Icon className="h-4 w-4" />
                    </span>
                )}
                <span>
                    <span className="block text-sm font-semibold text-slate-700">{label}</span>
                    {desc && <span className="block text-xs text-slate-400">{desc}</span>}
                </span>
            </span>
            <span className={`relative h-6 w-11 shrink-0 rounded-full transition ${checked ? "bg-indigo-600" : "bg-slate-300"}`}>
                <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${checked ? "left-[22px]" : "left-0.5"}`} />
            </span>
        </button>
    );
}

const inputCls =
    "w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none transition focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100";

export default function CreateTicketPage() {
    const [productId, setProductId] = useState('');
    const [eventName, setEventName] = useState("");
    const [date, setDate] = useState("");
    const [time, setTime] = useState("");
    const [address, setAddress] = useState("");

    const [email, setEmail] = useState("");
    const [firstName, setFirstName] = useState("");
    const [paymentId, setPaymentId] = useState("");
    const [attendee, setAttendee] = useState([""]);

    const [sendEmail, setSendEmail] = useState(true);
    const [force, setForce] = useState(false);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [result, setResult] = useState(null);

    const isCustom = productId === "custom";

    // const cleanedAttendees = useMemo(
    //     () => attendees.map((a) => a.trim()).filter(Boolean),
    //     [attendees]
    // );

    // const setAttendee = (i, val) =>
    //     setAttendees((arr) => arr.map((a, idx) => (idx === i ? val : a)));
    // const addAttendee = () => setAttendees((arr) => [...arr, ""]);
    // const removeAttendee = (i) =>
    //     setAttendees((arr) => (arr.length > 1 ? arr.filter((_, idx) => idx !== i) : arr));

    const validate = () => {
        if (!isEmail(email)) return "Enter a valid buyer email.";
        if (!attendee) return "Enter a valid name"
        return "";
    };

    const handleSubmit = async () => {
        setError("");
        setResult(null);
        const v = validate();
        if (v) return setError(v);

        const payload = {
            paymentId: paymentId.trim(),
            email: email.trim(),
            name: attendee,
            productId: productId,
            event: {
                eventName: eventName,
                date: date,
                time: time,
                address: address,
            }
        };

        setLoading(true);
        try {
            const res = await Apirequests("tickets/create", "POST", payload, {
                authorization: `Bearer ${getToken()}`
            });

            if (res?.success) {
                SuccessTost(res?.message || "Ticket created successfully.")
                setResult(res);
            } else {
                setError(res?.message || "Something went wrong");
            }

        } catch (e) {
            setError(e.message || "Could not create ticket.");
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setResult(null);
        setEmail("");
        setAttendee("");
        setPaymentId("");
        setProductId("");
        // setAttendees([""]);
    };

    return (
        <div className="min-h-screen bg-slate-50 p-4 sm:p-6">
            <div className="mx-auto max-w-2xl">
                {/* Header */}
                <div className="mb-5 flex items-center gap-2.5">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-600 text-white">
                        <TicketIcon className="h-5 w-5" />
                    </div>
                    <div>
                        <h1 className="text-xl font-bold text-slate-800">Create Ticket</h1>
                        <p className="text-sm text-slate-500">Manually issue a ticket for a buyer</p>
                    </div>
                </div>

                {/* Success result */}
                {result ? (
                    <div className="rounded-xl border border-slate-200 bg-white p-5">
                        <div className="mb-4 flex items-center gap-2 rounded-lg bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700">
                            <CheckCircle2 className="h-5 w-5" />
                            {result?.message}
                        </div>

                        <div className="divide-y divide-slate-100">
                            {/* {(result.tickets || []).map((t) => ( */}
                            <div className="flex items-center justify-between gap-3 py-3">
                                {result?.data?.ticketUrl && (
                                    <a
                                        href={result?.data?.ticketUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-semibold text-indigo-600 hover:bg-slate-50"
                                    >
                                        View <ExternalLink className="h-3.5 w-3.5" />
                                    </a>
                                )}
                            </div>
                            {/* ))} */}
                        </div>

                        <button
                            onClick={resetForm}
                            className="mt-4 w-full rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-indigo-700"
                        >
                            Create another
                        </button>
                    </div>
                ) : (
                    /* Form */
                    <div className="space-y-5 rounded-xl border border-slate-200 bg-white p-5">
                        {error && (
                            <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
                                <AlertCircle className="h-4 w-4" /> {error}
                            </div>
                        )}
                        <div className="space-y-4 rounded-lg border border-dashed border-slate-200 p-4">
                            <Field label="Event name" icon={TicketIcon}>
                                <input value={eventName} onChange={(e) => setEventName(e.target.value)} placeholder="Silva Method Live Seminar" className={inputCls} />
                            </Field>
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                <Field label="Date" icon={CalendarDays}>
                                    <input value={date} onChange={(e) => setDate(e.target.value)} placeholder="26th April, 2026" className={inputCls} />
                                </Field>
                                <Field label="Time" icon={Clock}>
                                    <input value={time} onChange={(e) => setTime(e.target.value)} placeholder="2:30 PM" className={inputCls} />
                                </Field>
                            </div>
                            <Field label="Venue / address" icon={MapPin}>
                                <input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Hotel Park Land, Durgapur…" className={inputCls} />
                            </Field>
                        </div>


                        {/* Buyer */}
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <Field label="Buyer email" icon={Mail}>
                                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="buyer@example.com" className={inputCls} />
                            </Field>
                            <Field label="Product Id" icon={Box} hint="Optional — product id">
                                <input value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="live-seminar-mumbai" className={inputCls} />
                            </Field>
                        </div>

                        <Field label="Payment ID" icon={CreditCard} hint="Optional — link to a real order. Leave blank for a manual ticket.">
                            <input value={paymentId} onChange={(e) => setPaymentId(e.target.value)} placeholder="pay_abc123" className={inputCls} />
                        </Field>

                        {/* Attendees */}
                        <div>
                            <span className="mb-1.5 flex items-center gap-1.5 text-sm font-semibold text-slate-700">
                                <Users className="h-4 w-4 text-slate-400" /> Attendee
                            </span>
                            <div className="space-y-2">
                                {/* {attendees.map((a, i) => ( */}
                                <div className="flex items-center gap-2">
                                    <input
                                        value={attendee}
                                        onChange={(e) => setAttendee(e.target.value)}
                                        placeholder={`Attendee name`}
                                        className={inputCls}
                                    />
                                    {/* <button
                                            type="button"
                                            onClick={() => removeAttendee(i)}
                                            disabled={attendees.length === 1}
                                            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-slate-200 text-slate-400 transition hover:bg-slate-50 hover:text-red-500 disabled:opacity-40"
                                            aria-label="Remove attendee"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </button> */}
                                </div>
                                {/* ))} */}
                            </div>
                            {/* <button
                                type="button"
                                onClick={addAttendee}
                                className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-indigo-600 hover:text-indigo-800"
                            >
                                <Plus className="h-4 w-4" /> Add attendee
                            </button>
                            <p className="mt-1 text-xs text-slate-400">Leave empty to issue a single ticket under the buyer.</p> */}
                        </div>

                        {/* Options */}
                        {/* <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Toggle checked={sendEmail} onChange={setSendEmail} icon={Send} label="Send email" desc="Email the ticket(s) to the buyer" />
              <Toggle checked={force} onChange={setForce} icon={RefreshCw} label="Force regenerate" desc="Overwrite an existing ticket image" />
            </div> */}

                        {/* Submit */}
                        <button
                            onClick={handleSubmit}
                            disabled={loading}
                            className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-3 text-sm font-bold text-white transition hover:bg-indigo-700 disabled:opacity-60"
                        >
                            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <TicketIcon className="h-4 w-4" />}
                            {loading ? "Creating…" : "Create ticket"}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}